/**
 * ID Format Utilities
 * 
 * Utilities for converting between UI tag representation and backend auto_gen_format string.
 * 
 * Backend format uses tokens like:
 * - {STUDY_ID} - Study ID
 * - {SITE_ID} - Site ID
 * - {INCR:100} - Incremental starting from 100
 * - Plain text - Constant values
 * 
 * Example: "REG-{STUDY_ID}-{SITE_ID}-{INCR:100}"
 */

import { TAG_TYPES } from './idFormatConstants'

/**
 * Parse auto_gen_format string into tags array for UI
 * @param {string} formatString - Backend format string (e.g., "REG-{SITE_ID}-{INCR:100}")
 * @returns {Array} Array of tag objects
 */
export const parseFormatString = (formatString) => {
    if (!formatString) return []

    const tags = []
    let currentPos = 0
    const tokenRegex = /\{([^}]+)\}/g
    let match

    const pushConstantText = (text) => {
        if (!text) return
        if (text === '-') {
            tags.push({ type: TAG_TYPES.HYPHEN, value: '' })
            return
        }
        tags.push({ type: TAG_TYPES.CONSTANT_VALUE, value: text })
    }

    // Map of token prefixes to TAG_TYPES for compact parsing
    const tokenParsers = [
        { prefix: 'INCR:', type: TAG_TYPES.INCREMENTAL_DIGITS },
        { prefix: 'LETTERS:', type: TAG_TYPES.LETTERS_UPPERCASE },
        { prefix: 'LETTERS_LOWER:', type: TAG_TYPES.LETTERS_LOWERCASE },
        { prefix: 'LETTERS_MIXED:', type: TAG_TYPES.LETTERS_MIXED },
        { prefix: 'DIGITS:', type: TAG_TYPES.DIGITS },
        { prefix: 'ALNUM:', type: TAG_TYPES.ALPHANUMERIC_UPPERCASE },
        { prefix: 'ALNUM_LOWER:', type: TAG_TYPES.ALPHANUMERIC_LOWERCASE },
        { prefix: 'ALNUM_MIXED:', type: TAG_TYPES.ALPHANUMERIC_MIXED },
    ]

    while ((match = tokenRegex.exec(formatString)) !== null) {
        // Add any constant text before this token
        if (match.index > currentPos) {
            const constantText = formatString.substring(currentPos, match.index)
            pushConstantText(constantText)
        }

        // Parse the token
        const tokenContent = match[1]

        if (tokenContent === 'STUDY_ID') {
            tags.push({ type: TAG_TYPES.STUDY_ID, value: '' })
        } else if (tokenContent === 'SITE_ID') {
            tags.push({ type: TAG_TYPES.SITE_ID, value: '' })
        } else {
            // Use prefix-based parser table
            for (let i = 0; i < tokenParsers.length; i += 1) {
                const { prefix, type } = tokenParsers[i]
                if (tokenContent.startsWith(prefix)) {
                    const value = tokenContent.substring(prefix.length)
                    tags.push({ type, value })
                    break
                }
            }
        }

        currentPos = match.index + match[0].length
    }

    // Add any remaining constant text after the last token
    if (currentPos < formatString.length) {
        const constantText = formatString.substring(currentPos)
        pushConstantText(constantText)
    }

    return tags
}

/**
 * Convert tags array to auto_gen_format string for backend
 * @param {Array} tags - Array of tag objects
 * @returns {string} Backend format string
 */
export const generateFormatString = (tags) => {
    if (!tags || tags.length === 0) return ''

    const formatters = {
        [TAG_TYPES.STUDY_ID]: () => '{STUDY_ID}',
        [TAG_TYPES.SITE_ID]: () => '{SITE_ID}',
        [TAG_TYPES.INCREMENTAL_DIGITS]: (tag) => `\{INCR:${tag.value || '0001'}\}`.replace('\\', ''),
        [TAG_TYPES.LETTERS_UPPERCASE]: (tag) => `{LETTERS:${tag.value || '4'}}`,
        [TAG_TYPES.LETTERS_LOWERCASE]: (tag) => `{LETTERS_LOWER:${tag.value || '4'}}`,
        [TAG_TYPES.LETTERS_MIXED]: (tag) => `{LETTERS_MIXED:${tag.value || '4'}}`,
        [TAG_TYPES.DIGITS]: (tag) => `{DIGITS:${tag.value || '4'}}`,
        [TAG_TYPES.ALPHANUMERIC_UPPERCASE]: (tag) => `{ALNUM:${tag.value || '4'}}`,
        [TAG_TYPES.ALPHANUMERIC_LOWERCASE]: (tag) => `{ALNUM_LOWER:${tag.value || '4'}}`,
        [TAG_TYPES.ALPHANUMERIC_MIXED]: (tag) => `{ALNUM_MIXED:${tag.value || '4'}}`,
        [TAG_TYPES.HYPHEN]: () => '-',
        [TAG_TYPES.CONSTANT_VALUE]: (tag) => tag.value || '',
    }

    return tags
        .map(tag => (formatters[tag.type] ? formatters[tag.type](tag) : ''))
        .join('')
}

/**
 * Find a field in participant_data_fields by internal_key
 * @param {Object} participantDataFields - The participant_data_fields config object
 * @param {string} internalKey - The internal_key to search for
 * @returns {Object|null} The field object or null if not found
 */
export const findFieldByKey = (participantDataFields, internalKey) => {
    if (!participantDataFields) return null

    const searchField = (fields) =>
        fields?.find(f => f.metadata?.internal_key === internalKey) || null

    return (
        searchField(participantDataFields.mandatory) ||
        searchField(participantDataFields.optional)
    )
}

/**
 * Update a field in participant_data_fields
 * @param {Object} participantDataFields - The participant_data_fields config object
 * @param {string} internalKey - The internal_key to update
 * @param {Object} updates - Updates to apply to the field's metadata
 * @returns {Object} Updated participant_data_fields
 */
export const updateField = (participantDataFields, internalKey, updates) => {
    const newFields = JSON.parse(JSON.stringify(participantDataFields || { mandatory: [], optional: [] }))

    // Helper to update field in an array
    const updateInArray = (array) => {
        const index = array.findIndex(f => f.metadata?.internal_key === internalKey)
        if (index !== -1) {
            array[index].metadata = {
                ...array[index].metadata,
                ...updates,
            }
            return true
        }
        return false
    }

    // Try to update in mandatory, then optional
    if (newFields.mandatory && !updateInArray(newFields.mandatory)) {
        if (newFields.optional) {
            updateInArray(newFields.optional)
        }
    }

    return newFields
}

/**
 * Ensure a field exists in participant_data_fields
 * @param {Object} participantDataFields - The participant_data_fields config object
 * @param {string} internalKey - The internal_key
 * @param {string} displayLabel - The display label
 * @param {boolean} isMandatory - Whether the field should be in mandatory array
 * @returns {Object} Updated participant_data_fields
 */
export const ensureFieldExists = (participantDataFields, internalKey, displayLabel, isMandatory = false) => {
    const newFields = JSON.parse(JSON.stringify(participantDataFields || { mandatory: [], optional: [] }))

    // Check if field already exists
    const existsInMandatory = newFields.mandatory?.some(f => f.metadata?.internal_key === internalKey)
    const existsInOptional = newFields.optional?.some(f => f.metadata?.internal_key === internalKey)

    if (existsInMandatory || existsInOptional) {
        return newFields
    }

    // Create the field
    const field = {
        display_label: displayLabel,
        type: 'text',
        metadata: {
            internal_key: internalKey,
            custom_validations: '',
            is_custom: false,
            default_value: '',
            can_update: true,
            is_auto_generated: false,
            auto_gen_format: '',
        },
    }

    // Add to appropriate array
    if (isMandatory) {
        if (!newFields.mandatory) newFields.mandatory = []
        newFields.mandatory.push(field)
    } else {
        if (!newFields.optional) newFields.optional = []
        newFields.optional.push(field)
    }

    return newFields
}

/**
 * Move a field between mandatory and optional based on required flag
 * If the field doesn't exist, no-op.
 */
export const relocateFieldByRequired = (participantDataFields, internalKey, isMandatory) => {
    const newFields = JSON.parse(JSON.stringify(participantDataFields || { mandatory: [], optional: [] }))

    // Find and remove from current location
    const removeFrom = (arr) => {
        const idx = arr.findIndex(f => f.metadata?.internal_key === internalKey)
        if (idx !== -1) {
            return arr.splice(idx, 1)[0]
        }
        return null
    }

    let field = null
    if (newFields.mandatory) field = removeFrom(newFields.mandatory)
    if (!field && newFields.optional) field = removeFrom(newFields.optional)
    if (!field) return newFields

    // Insert into target location
    if (isMandatory) {
        if (!newFields.mandatory) newFields.mandatory = []
        newFields.mandatory.push(field)
    } else {
        if (!newFields.optional) newFields.optional = []
        newFields.optional.push(field)
    }

    return newFields
}

